import type { WorldBbox } from "@/utils/plan-analysis/types";

/**
 * Repères des observations sur le plan : une lettre par observation (A, B…
 * Z, AA, AB…), attribuée par ordre de création, l'emprise du cadre bleu qui
 * entoure ses entités, et de quoi les classer par partie depuis le plan.
 * Fonctions pures — aucun accès au moteur ni au réseau.
 */

const ALPHABET_SIZE = 26;
const CHAR_CODE_A = 65;

/**
 * Lettre du rang `index` (0 → A, 25 → Z, 26 → AA, 27 → AB…) — numérotation
 * bijective en base 26, celle des colonnes d'un tableur.
 */
export function observationLetter(index: number): string {
  if (!Number.isInteger(index) || index < 0) return "";
  let rank = index + 1;
  let letters = "";
  while (rank > 0) {
    const digit = (rank - 1) % ALPHABET_SIZE;
    letters = String.fromCharCode(CHAR_CODE_A + digit) + letters;
    rank = Math.floor((rank - 1) / ALPHABET_SIZE);
  }
  return letters;
}

/** Date de création en ms ; une date illisible compte comme la plus ancienne. */
function creationTime(createdAt: string): number {
  const time = new Date(createdAt).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/**
 * Observations de la première à la dernière créée (date, puis id pour
 * départager) — l'ordre de la liste et celui des lettres. Rend une copie.
 */
export function sortByCreation<T extends { id: number; createdAt: string }>(
  observations: ReadonlyArray<T>,
): T[] {
  return [...observations].sort(
    (a, b) =>
      creationTime(a.createdAt) - creationTime(b.createdAt) || a.id - b.id,
  );
}

/**
 * Lettre de chaque observation, par id : la plus ancienne porte A. L'ordre
 * de création fait foi — un filtre de la liste ne change donc jamais la
 * lettre d'une observation.
 */
export function buildObservationLetters(
  observations: ReadonlyArray<{ id: number; createdAt: string }>,
): Map<number, string> {
  return new Map(
    sortByCreation(observations).map((obs, index) => [
      obs.id,
      observationLetter(index),
    ]),
  );
}

/** Emprise englobant toutes celles fournies (les illisibles sont ignorées). */
export function unionBbox(
  bboxes: ReadonlyArray<WorldBbox | null | undefined>,
): WorldBbox | null {
  let union: WorldBbox | null = null;
  for (const bbox of bboxes) {
    if (!bbox) continue;
    union = union
      ? {
          minX: Math.min(union.minX, bbox.minX),
          minY: Math.min(union.minY, bbox.minY),
          maxX: Math.max(union.maxX, bbox.maxX),
          maxY: Math.max(union.maxY, bbox.maxY),
        }
      : { ...bbox };
  }
  return union;
}

/** Marge minimale autour d'une emprise ponctuelle (unités du plan). */
const MIN_ZOOM_PADDING = 2;

/**
 * Fenêtre de zoom qui montre une emprise avec de l'air autour : une marge
 * égale à son plus grand côté (au moins 2 unités, pour un élément ponctuel) —
 * la même que « Voir sur le plan ».
 */
export function zoomWindowAround(bbox: WorldBbox): WorldBbox {
  const padding = Math.max(
    bbox.maxX - bbox.minX,
    bbox.maxY - bbox.minY,
    MIN_ZOOM_PADDING,
  );
  return {
    minX: bbox.minX - padding,
    minY: bbox.minY - padding,
    maxX: bbox.maxX + padding,
    maxY: bbox.maxY + padding,
  };
}

// ── Classement par partie (onglet Parties) ──────────────────────────────────

/** Nombre d'éléments par partie ; ceux sans partie ne sont pas comptés. */
export function countByPart<T>(
  items: ReadonlyArray<T>,
  partIdOf: (item: T) => number | null,
): Map<number, number> {
  const counts = new Map<number, number>();
  for (const item of items) {
    const partId = partIdOf(item);
    if (partId === null) continue;
    counts.set(partId, (counts.get(partId) ?? 0) + 1);
  }
  return counts;
}

/** Cadre repéré, en pixels écran. */
export interface IdentifiedRect {
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Zone tracée à l'écran, donnée par deux coins opposés quelconques. */
export interface ScreenZone {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * Cadres « dans la zone » : ceux dont le CENTRE y est. Un cadre à cheval sur
 * le bord est pris s'il est majoritairement dedans — pas besoin de
 * l'entourer entièrement, ce qui serait pénible au doigt.
 */
export function idsCenteredIn(
  rects: ReadonlyArray<IdentifiedRect>,
  zone: ScreenZone,
): string[] {
  const left = Math.min(zone.x0, zone.x1);
  const right = Math.max(zone.x0, zone.x1);
  const top = Math.min(zone.y0, zone.y1);
  const bottom = Math.max(zone.y0, zone.y1);
  return rects
    .filter((rect) => {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      return (
        centerX >= left && centerX <= right && centerY >= top && centerY <= bottom
      );
    })
    .map((rect) => rect.id);
}
